/**
 * Process-wide Notification React context singleton.
 *
 * Stale fingerprinted `/assets/js/main-*.js` URLs (SW/CDN/deploy mix) can
 * evaluate this module twice under different module URLs. Each evaluation would
 * otherwise call `createContext()` again → Provider from chunk A, useNotification
 * from chunk B → "useNotification must be used within a NotificationProvider"
 * (ST-2026-158, same class as ST-2026-152).
 *
 * Storing the Context on globalThis keeps identity stable across duplicate loads.
 */
import { createContext, type Context } from 'react';
import type { AlertColor } from '@mui/material/Alert';

export interface NotificationContextType {
  showNotification: (message: string, severity?: AlertColor, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
}

export const APT_NOTIFICATION_REACT_CONTEXT_GLOBAL_KEY =
  '__APT_NOTIFICATION_REACT_CONTEXT__' as const;

type AptNotificationReactContextGlobal = typeof globalThis & {
  [APT_NOTIFICATION_REACT_CONTEXT_GLOBAL_KEY]?: Context<NotificationContextType | undefined>;
};

export function getNotificationReactContext(): Context<NotificationContextType | undefined> {
  const g = globalThis as AptNotificationReactContextGlobal;
  if (!g[APT_NOTIFICATION_REACT_CONTEXT_GLOBAL_KEY]) {
    g[APT_NOTIFICATION_REACT_CONTEXT_GLOBAL_KEY] = createContext(
      undefined,
    ) as Context<NotificationContextType | undefined>;
  }
  return g[APT_NOTIFICATION_REACT_CONTEXT_GLOBAL_KEY];
}

/** Test helper: drop the singleton so suites can isolate identity. */
export function resetNotificationReactContextForTests(): void {
  const g = globalThis as AptNotificationReactContextGlobal;
  delete g[APT_NOTIFICATION_REACT_CONTEXT_GLOBAL_KEY];
}
